import React, { useState, useEffect } from 'react';
import VPIDataService from '../../services/vpiDataService';
import { masterSupabase as supabase } from '../../lib/supabase-master';
import { calculateIndexAdjustment } from '../../domain/indexAdjustments/calculateIndexAdjustment';
import { useTheme } from '../../contexts/ThemeContext';

// ── Ampel-Status-Logik ────────────────────────────────────────────────────────
const getTrafficLight = (calc) => {
  if (!calc) return null;
  const { thresholdMet, percentage, thresholdPercentage } = calc.vpi;
  if (thresholdMet) {
    return {
      color: 'green',
      dot: '🟢',
      label: 'Erhöhung möglich',
      badge: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
    };
  }
  const remaining = thresholdPercentage - percentage;
  if (remaining <= 5) {
    return {
      color: 'yellow',
      dot: '🟡',
      label: `Noch ${remaining.toFixed(1)} % bis Schwelle`,
      badge: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
    };
  }
  return {
    color: 'red',
    dot: '🔴',
    label: 'Prüfung erforderlich',
    badge: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  };
};

// ── KI-Executive-Summary ─────────────────────────────────────────────────────
const buildSummary = (calc, tenant) => {
  if (!calc) return '';
  const { percentage, thresholdPercentage, thresholdMet, baseDate, currentDate } = calc.vpi;
  const base = baseDate
    ? new Date(baseDate + '-01').toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })
    : 'Vertragsbeginn';
  const current = currentDate
    ? new Date(currentDate + '-01').toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })
    : 'aktuell';
  if (thresholdMet) {
    return `Der Verbraucherpreisindex ist seit ${base} um ${percentage.toFixed(2)} % gestiegen und überschreitet die vertragliche Anpassungsschwelle von ${thresholdPercentage} %. Eine Mieterhöhung ist zulässig.`;
  }
  return `Der Verbraucherpreisindex ist seit ${base} um ${percentage.toFixed(2)} % gestiegen. Die vertragliche Anpassungsschwelle von ${thresholdPercentage} % ist aktuell noch nicht erreicht. Eine Mieterhöhung ist derzeit nicht möglich.`;
};

// ── Datumsformatierung ────────────────────────────────────────────────────────
const formatDate = (dateStr) => {
  if (!dateStr) return '–';
  try {
    return new Date(dateStr).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return dateStr;
  }
};

// ── Hauptkomponente ───────────────────────────────────────────────────────────
const IndexAdjustmentReviewCard = ({ fact, executeAction }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const [tenant, setTenant] = useState(null);
  const [unit, setUnit] = useState(null);
  const [property, setProperty] = useState(null);
  const [calculation, setCalculation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const tc = {
    bg:            isDark ? 'bg-gray-900'        : 'bg-white',
    text:          isDark ? 'text-gray-100'       : 'text-gray-900',
    textSub:       isDark ? 'text-gray-400'       : 'text-gray-500',
    border:        isDark ? 'border-gray-700/60'  : 'border-gray-200',
    highlight:     isDark ? 'bg-gray-800/60'      : 'bg-gray-50',
    summaryBg:     isDark ? 'bg-blue-950/40 border-blue-800/40' : 'bg-blue-50 border-blue-200',
    summaryText:   isDark ? 'text-blue-200'       : 'text-blue-800',
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // 1. Mieter laden (mit ID aus dem Briefing-Fact)
        const tenantId = fact.entity_ref.id;
        let tenantData = null;
        const isUUID = /^[0-9a-f-]{36}$/i.test(tenantId);
        if (isUUID) {
          // maybeSingle() returns null (not an error) when no row is found
          const { data: tById, error: tErr } = await supabase
            .from('tenants')
            .select('*')
            .eq('id', tenantId)
            .maybeSingle();
          if (tErr) throw tErr;
          tenantData = tById;
        }
        // Fallback: load first available tenant (for mock/demo IDs)
        if (!tenantData) {
          const { data: tFallback, error: tFallbackErr } = await supabase
            .from('tenants')
            .select('*')
            .limit(1);
          if (tFallbackErr) throw tFallbackErr;
          tenantData = tFallback?.[0] || null;
        }
        if (!tenantData) throw new Error('Kein Mieter gefunden');
        setTenant(tenantData);

        // 2. Einheit und Objekt laden
        let uData = null;
        let pData = null;
        if (tenantData.unit_id) {
          const { data: unitResult } = await supabase
            .from('units')
            .select('*')
            .eq('id', tenantData.unit_id)
            .maybeSingle();
          uData = unitResult;
          setUnit(uData);
          if (uData?.property_id) {
            const { data: propResult } = await supabase
              .from('properties')
              .select('*')
              .eq('id', uData.property_id)
              .maybeSingle();
            pData = propResult;
            setProperty(pData);
          }
        }

        // 3. VPI-Daten laden (VPIDataService – identisch zu LeaseAdjustments.jsx)
        await VPIDataService.loadData();
        const allVpiData = await VPIDataService.getAllData();
        if (!allVpiData || allVpiData.length === 0) throw new Error('VPI-Daten konnten nicht geladen werden');

        // 4. Berechnung
        const calc = calculateIndexAdjustment(tenantData, uData, pData, { historicalData: allVpiData });
        setCalculation(calc);

      } catch (err) {
        console.error('IndexAdjustmentReviewCard error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [fact.entity_ref.id]);

  const handleAction = (actionName) => {
    if (!fact.allowed_actions.includes(actionName)) return;
    if (actionName === 'create_mail_draft') {
      alert('Mailentwurf erstellen – folgt in Gate 2');
    } else {
      executeAction?.({ action: actionName, factId: fact.fact_id, source: 'button' });
    }
  };

  // ── Loading / Error States ─────────────────────────────────────────────────
  if (loading) {
    return (
      <div className={`p-5 rounded-xl border ${tc.border} ${tc.bg} animate-pulse space-y-3`}>
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
        <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full" />
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-5 rounded-xl border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/30">
        <p className="text-red-700 dark:text-red-400 font-medium text-sm">⚠️ {error}</p>
      </div>
    );
  }

  if (!calculation) {
    return (
      <div className={`p-5 rounded-xl border ${tc.border} ${tc.bg}`}>
        <p className={`text-sm ${tc.textSub}`}>Keine Berechnung möglich – fehlende Vertragsdaten.</p>
      </div>
    );
  }

  const light = getTrafficLight(calculation);
  const summary = buildSummary(calculation, tenant);
  const { vpi, currentRent } = calculation;
  const canIncrease = vpi.thresholdMet;

  // Adresse aus Property
  const address = property
    ? [property.street, property.postal_code, property.city].filter(Boolean).join(' ') || property.name
    : null;

  // Indexmiete seit (Vertragsbeginn)
  const indexSince = formatDate(tenant?.base_rent_date || tenant?.lease_start);

  // Hero-Betrag: Farbe nur grün wenn Erhöhung zulässig
  const heroColor = canIncrease
    ? 'text-green-500 dark:text-green-400'
    : 'text-blue-400 dark:text-blue-300';

  return (
    <div className={`rounded-xl border ${tc.border} ${tc.bg} overflow-hidden shadow-sm`}>

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className={`px-5 py-4 border-b ${tc.border} ${tc.highlight} flex justify-between items-center`}>
        <div className="flex items-center gap-2">
          <span className="text-lg">📈</span>
          <span className={`font-semibold text-sm ${tc.text}`}>Indexanpassung prüfen</span>
        </div>
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${light.badge}`}>
          {light.dot} {light.label}
        </span>
      </div>

      <div className="p-5 space-y-5">

        {/* ── KI Executive Summary ──────────────────────────────────────────── */}
        <div className={`p-3.5 rounded-lg border text-sm leading-relaxed ${tc.summaryBg} ${tc.summaryText}`}>
          <span className="font-semibold mr-1">Clara:</span>{summary}
        </div>

        {/* ── Hero-Kennzahl + Mieterkontext ─────────────────────────────────── */}
        <div className="flex justify-between items-start gap-4">
          {/* Mieterkontext */}
          <div className="min-w-0">
            <p className={`text-xs uppercase tracking-wide font-medium ${tc.textSub} mb-0.5`}>Mieter</p>
            <p className={`font-semibold ${tc.text} truncate`}>{tenant?.name || 'Unbekannt'}</p>
            {address && (
              <p className={`text-xs ${tc.textSub} mt-0.5 truncate`}>{address}</p>
            )}
            <p className={`text-xs ${tc.textSub} mt-0.5`}>
              {unit?.unit_number ? `Einheit ${unit.unit_number}` : property?.name || ''}
              {' · '}Indexmiete seit {indexSince}
            </p>
          </div>

          {/* Hero-Betrag */}
          <div className="text-right flex-shrink-0">
            <p className={`text-xs uppercase tracking-wide font-medium ${tc.textSub} mb-0.5`}>
              {canIncrease ? 'Mögliche Erhöhung' : 'Berechneter Betrag'}
            </p>
            <p className={`text-3xl font-bold tabular-nums ${heroColor}`}>
              +{vpi.amount.toFixed(2)} €
            </p>
            <p className={`text-xs ${tc.textSub} mt-0.5`}>
              Neu: {vpi.newRent.toFixed(2)} € / Monat
            </p>
          </div>
        </div>

        {/* ── VPI-Nachweis ──────────────────────────────────────────────────── */}
        <div className={`rounded-lg border ${tc.border} ${tc.highlight} divide-y ${tc.border}`}>
          <div className="flex justify-between items-center px-4 py-2.5 text-sm">
            <span className={tc.textSub}>Basis-Index ({vpi.baseDate || '–'})</span>
            <span className={`font-medium tabular-nums ${tc.text}`}>
              {vpi.baseIndex != null ? vpi.baseIndex.toFixed(1) : '–'} Pkt.
            </span>
          </div>
          <div className="flex justify-between items-center px-4 py-2.5 text-sm">
            <span className={tc.textSub}>Aktueller Index ({vpi.currentDate || '–'})</span>
            <span className={`font-medium tabular-nums ${tc.text}`}>
              {vpi.currentIndex != null ? vpi.currentIndex.toFixed(1) : '–'} Pkt.
            </span>
          </div>
          <div className="flex justify-between items-center px-4 py-2.5 text-sm">
            <span className={tc.textSub}>Steigerung</span>
            <span className={`font-semibold tabular-nums ${tc.text}`}>
              {vpi.percentage.toFixed(2)} %
            </span>
          </div>
          <div className="flex justify-between items-center px-4 py-2.5 text-sm">
            <span className={tc.textSub}>Aktuelle Miete</span>
            <span className={`font-medium tabular-nums ${tc.text}`}>
              {currentRent.toFixed(2)} € / Monat
            </span>
          </div>
        </div>

        {/* ── Datenfrische ──────────────────────────────────────────────────── */}
        <p className={`text-xs ${tc.textSub} italic`}>
          Datenfrische: VPI Stand {fact.data_freshness?.vpi_as_of || vpi.currentDate || '–'}
          {vpi.isEstimated && ' · Schätzwert (kein exakter Basiszeitraum gefunden)'}
        </p>

        {/* ── Aktionen ──────────────────────────────────────────────────────── */}
        <div className="pt-1 flex flex-col gap-2">
          {/* Primäraktion */}
          {fact.allowed_actions.includes('open_calculation') && (
            <button
              onClick={() => handleAction('open_calculation')}
              className="w-full py-2.5 px-4 rounded-lg bg-blue-600 hover:bg-blue-500 active:bg-blue-700 text-white text-sm font-semibold transition-colors shadow-sm"
            >
              Berechnung prüfen →
            </button>
          )}

          {/* Sekundäraktionen */}
          <div className="flex gap-2">
            {fact.allowed_actions.includes('create_mail_draft') && canIncrease && (
              <button
                onClick={() => handleAction('create_mail_draft')}
                className={`flex-1 py-2 px-3 rounded-lg border ${tc.border} hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-sm font-medium ${tc.text}`}
              >
                Entwurf erstellen
              </button>
            )}
            <button
              onClick={() => handleAction('open_calculation')}
              className={`flex-1 py-2 px-3 rounded-lg border ${tc.border} hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-sm font-medium ${tc.textSub}`}
            >
              Vertragsdetails
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default IndexAdjustmentReviewCard;
